import { useState, useRef } from 'react';
import { Property } from '@/types/property';
import { formatPrice, formatNumber, getPropertyImage } from '@/lib/formatters';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription 
} from '@/components/ui/dialog';
import { 
  Bed, Bath, Square, MapPin, Copy, Download, Facebook, 
  Instagram, Sparkles, Camera, Home, Car
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface MarketingPostGeneratorProps {
  property: Property;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type TemplateStyle = 'modern' | 'elegant' | 'bold' | 'minimal';

export function MarketingPostGenerator({ property, open, onOpenChange }: MarketingPostGeneratorProps) {
  const [selectedStyle, setSelectedStyle] = useState<TemplateStyle>('modern');
  const postRef = useRef<HTMLDivElement>(null);

  const shareUrl = typeof window !== 'undefined' ? `${window.location.origin}/property/${property.slug || property.id}` : '';

  const generateCaption = () => {
    const emoji = {
      bed: '🛏️',
      bath: '🛁',
      sqft: '📐',
      price: '💰',
      location: '📍',
      home: '🏡',
      sparkle: '✨',
      fire: '🔥',
      key: '🔑',
      star: '⭐',
    };

    const features = property.features?.slice(0, 3).join(' • ') || '';
    
    return `${emoji.fire} NEW LISTING ALERT ${emoji.fire}

${emoji.home} ${property.title}
${emoji.location} ${property.address}, ${property.city}

${emoji.price} ${formatPrice(property.price)}

${emoji.bed} ${property.bedrooms} Bedrooms
${emoji.bath} ${property.bathrooms} Bathrooms
${property.sqft ? `${emoji.sqft} ${formatNumber(property.sqft)} sqft` : ''}
${property.parking ? `🚗 ${property.parking}` : ''}

${features ? `${emoji.star} Features: ${features}` : ''}

${emoji.sparkle} Book your viewing today!
${emoji.key} Link in bio or visit: ${shareUrl}

#RealEstate #${property.city.replace(/\s/g, '')}Homes #HomeForSale #DreamHome #PropertyListing #HouseHunting #NewListing #${property.neighborhood?.replace(/\s/g, '') || 'Edmonton'}`;
  };

  const handleCopyCaption = async () => {
    await navigator.clipboard.writeText(generateCaption());
    toast.success('Caption copied! Ready to paste in your social post');
  };

  const handleDownloadImage = async () => {
    // Open primary image in new tab for download
    const imageUrl = getPropertyImage(property.images, 0);
    window.open(imageUrl, '_blank');
    toast.success('Image opened - right click to save');
  };

  const handleShareFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(generateCaption().substring(0, 500))}`,
      '_blank',
      'width=600,height=500'
    );
  };

  const styles: Record<TemplateStyle, { bg: string; accent: string; text: string; badge: string }> = {
    modern: {
      bg: 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900',
      accent: 'text-emerald-400',
      text: 'text-white',
      badge: 'bg-emerald-500 text-white',
    },
    elegant: {
      bg: 'bg-gradient-to-br from-amber-50 via-stone-100 to-amber-50',
      accent: 'text-amber-700',
      text: 'text-stone-800',
      badge: 'bg-amber-600 text-white',
    },
    bold: {
      bg: 'bg-gradient-to-br from-rose-600 via-pink-600 to-purple-700',
      accent: 'text-yellow-300',
      text: 'text-white',
      badge: 'bg-yellow-400 text-black',
    },
    minimal: {
      bg: 'bg-white',
      accent: 'text-blue-600',
      text: 'text-gray-900',
      badge: 'bg-blue-600 text-white',
    },
  };

  const currentStyle = styles[selectedStyle];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Create Marketing Post
          </DialogTitle>
          <DialogDescription>
            Generate a professional social media post for Facebook & Instagram
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6 mt-4">
          {/* Preview Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Preview</h3>
              <div className="flex gap-1">
                {(Object.keys(styles) as TemplateStyle[]).map((style) => (
                  <Button
                    key={style}
                    variant={selectedStyle === style ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedStyle(style)}
                    className="capitalize text-xs"
                  >
                    {style}
                  </Button>
                ))}
              </div>
            </div>

            {/* Marketing Post Preview */}
            <div
              ref={postRef}
              className={cn(
                'aspect-square rounded-xl overflow-hidden relative shadow-2xl',
                currentStyle.bg
              )}
            >
              {/* Background Image with Overlay */}
              <div className="absolute inset-0">
                <img
                  src={getPropertyImage(property.images, 0)}
                  alt={property.title}
                  className="w-full h-full object-cover"
                />
                <div className={cn(
                  'absolute inset-0',
                  selectedStyle === 'modern' && 'bg-gradient-to-t from-black/90 via-black/40 to-transparent',
                  selectedStyle === 'elegant' && 'bg-gradient-to-t from-stone-900/80 via-stone-900/30 to-transparent',
                  selectedStyle === 'bold' && 'bg-gradient-to-t from-purple-900/90 via-rose-900/50 to-transparent',
                  selectedStyle === 'minimal' && 'bg-gradient-to-t from-white/95 via-white/60 to-transparent'
                )} />
              </div>

              {/* Content Overlay */}
              <div className="absolute inset-0 p-6 flex flex-col justify-between">
                {/* Top Section */}
                <div className="flex justify-between items-start">
                  <Badge className={cn('text-xs font-bold uppercase tracking-wider', currentStyle.badge)}>
                    🔥 New Listing
                  </Badge>
                  <div className={cn('text-right', currentStyle.text)}>
                    <Camera className="w-5 h-5 inline-block mr-1 opacity-70" />
                    <span className="text-sm opacity-70">{property.images?.length || 1} Photos</span>
                  </div>
                </div>

                {/* Bottom Section */}
                <div className="space-y-4">
                  {/* Price */}
                  <div>
                    <p className={cn('text-4xl md:text-5xl font-bold tracking-tight', currentStyle.accent)}>
                      {formatPrice(property.price)}
                    </p>
                  </div>

                  {/* Property Details */}
                  <div className={cn('space-y-2', currentStyle.text)}>
                    <h3 className="text-xl md:text-2xl font-semibold line-clamp-2">
                      {property.title}
                    </h3>
                    <div className="flex items-center gap-1 opacity-90">
                      <MapPin className="w-4 h-4" />
                      <span className="text-sm">
                        {property.neighborhood || property.city}, {property.city}
                      </span>
                    </div>
                  </div>

                  {/* Stats Row */}
                  <div className={cn(
                    'flex items-center gap-4 py-3 px-4 rounded-lg',
                    selectedStyle === 'minimal' ? 'bg-gray-100' : 'bg-white/10 backdrop-blur-sm'
                  )}>
                    <div className={cn('flex items-center gap-2', selectedStyle === 'minimal' ? 'text-gray-700' : 'text-white')}>
                      <Bed className="w-5 h-5" />
                      <span className="font-semibold">{property.bedrooms}</span>
                      <span className="text-sm opacity-70">Beds</span>
                    </div>
                    <div className="w-px h-6 bg-white/30" />
                    <div className={cn('flex items-center gap-2', selectedStyle === 'minimal' ? 'text-gray-700' : 'text-white')}>
                      <Bath className="w-5 h-5" />
                      <span className="font-semibold">{property.bathrooms}</span>
                      <span className="text-sm opacity-70">Baths</span>
                    </div>
                    {property.sqft && (
                      <>
                        <div className="w-px h-6 bg-white/30" />
                        <div className={cn('flex items-center gap-2', selectedStyle === 'minimal' ? 'text-gray-700' : 'text-white')}>
                          <Square className="w-5 h-5" />
                          <span className="font-semibold">{formatNumber(property.sqft)}</span>
                          <span className="text-sm opacity-70">sqft</span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* CTA */}
                  <div className={cn(
                    'text-center py-2 rounded-lg font-semibold',
                    currentStyle.badge
                  )}>
                    📱 Book Your Viewing Today!
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Caption & Actions Section */}
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2">Ready-to-Post Caption</h3>
              <Card className="p-4 bg-muted/50 max-h-[300px] overflow-y-auto">
                <pre className="text-sm whitespace-pre-wrap font-sans">
                  {generateCaption()}
                </pre>
              </Card>
            </div>

            <div className="space-y-3">
              <Button onClick={handleCopyCaption} className="w-full" variant="outline">
                <Copy className="w-4 h-4 mr-2" />
                Copy Caption
              </Button>

              <Button onClick={handleDownloadImage} className="w-full" variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Download Property Image
              </Button>

              <div className="grid grid-cols-3 gap-3">
                <Button 
                  onClick={handleShareFacebook}
                  className="bg-[#1877F2] hover:bg-[#166FE5] text-white"
                >
                  <Facebook className="w-4 h-4 mr-2" />
                  Facebook
                </Button>
                <Button 
                  onClick={() => {
                    handleCopyCaption();
                    toast.info('Caption copied! Open Instagram and paste', {
                      description: 'Instagram doesn\'t support direct sharing from web'
                    });
                  }}
                  className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:opacity-90 text-white"
                >
                  <Instagram className="w-4 h-4 mr-2" />
                  Instagram
                </Button>
                <Button 
                  onClick={() => {
                    const tiktokCaption = generateCaption() + '\n\n#TikTokRealEstate #HouseTour #PropertyTour #RealEstateAgent #LuxuryHome';
                    navigator.clipboard.writeText(tiktokCaption);
                    toast.info('Caption copied with TikTok hashtags!', {
                      description: 'Open TikTok and paste when posting your video'
                    });
                  }}
                  className="bg-black hover:bg-gray-900 text-white"
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                  TikTok
                </Button>
              </div>
            </div>

            {/* Step-by-step posting guide */}
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mt-4">
              <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                📱 How to Post (Best Results)
              </h4>
              <ol className="text-xs text-muted-foreground space-y-2">
                <li className="flex gap-2">
                  <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-[10px] font-bold">1</span>
                  <span><strong>Download</strong> the property image using the button above</span>
                </li>
                <li className="flex gap-2">
                  <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-[10px] font-bold">2</span>
                  <span><strong>Copy</strong> the ready-to-post caption with hashtags</span>
                </li>
                <li className="flex gap-2">
                  <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-[10px] font-bold">3</span>
                  <span><strong>Open</strong> Facebook/Instagram and create a new post</span>
                </li>
                <li className="flex gap-2">
                  <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-[10px] font-bold">4</span>
                  <span><strong>Upload</strong> the image, paste the caption, and post!</span>
                </li>
              </ol>
              <p className="text-[10px] text-muted-foreground mt-3 pt-2 border-t border-primary/10">
                💡 Posts with uploaded images get 3x more engagement than shared links!
              </p>
            </div>

            <div className="bg-muted/50 rounded-lg p-4">
              <h4 className="font-medium text-sm mb-2">⏰ Best Times to Post</h4>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• <strong>Facebook:</strong> 9-11 AM or 1-3 PM weekdays</li>
                <li>• <strong>Instagram:</strong> 11 AM-1 PM or 7-9 PM</li>
                <li>• <strong>TikTok:</strong> 7-9 PM or weekends</li>
              </ul>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}